import { COMPONENT_STATE_ARGS, Metadata, pick } from '../../../utils/spds/metadata';
import {
  deactivate,
  isActive,
  isCapturable,
  isCloseKey,
  isDownKey,
  isHorizontalArrowKey,
  isLeftKey,
  isRightKey,
  isSelectionKey,
  isShiftableKey,
  isVerticalArrowKey,
  isVerticalMovementKey,
  updateElementFocus,
} from '../../../utils/keyboard-nav-support';
import { indexInRange } from '../../../utils/spds/array-handling';
import { tracked } from '@glimmer/tracking';
import { waitForLater } from '../../../utils/waiters';
import Super from './base.gjs';

const MENU_OPEN_DELAY = 100;

const METADATA = Metadata.define({
  description: `
The keyboard-navigable base is an abstract core component that provides a reusable arrow-key navigation engine for any
SPDS component that exposes a list of focusable options behind an openable menu (search, dropdown list, filter buttons, and
future list/menu-style components). It does not render a template of its own; instead, components extend it and supply the
small set of configuration hooks described below.

### Navigation model

Navigation is built on the shared \`keyboard-nav-support\` utility. The currently focused option is tracked visually with the
CSS class \`active\` and programmatically via \`element.focus()\` (there is no roving \`tabindex\` or \`aria-activedescendant\`). The
engine listens for \`keydown\` on the component's \`baseElement\` (registered by the base-class \`setupComponent\` modifier) and routes
keys as follows:

* When the menu is closed, \`ArrowUp\` / \`ArrowDown\` / \`Enter\` open it (when \`menuCanOpen\` allows). Opening with an arrow key also
  moves to the first/last option after a short delay so the menu has time to render.
* When the menu is open, \`ArrowUp\` / \`ArrowDown\` (and \`Tab\` / \`Shift+Tab\`, treated as down / up) move the active option,
  \`ArrowLeft\` / \`ArrowRight\` traverse auxiliary controls when horizontal navigation is enabled (see below), \`Enter\` selects the
  active option, and \`Escape\` closes the menu.

### Horizontal navigation model

When \`allowHorizontalNavigation\` is enabled, the engine treats the component as an anchor with an arbitrarily-sized ordered list
of focusable controls on each side: \`leftNavigableOptions\` and \`rightNavigableOptions\` (index 0 nearest the anchor). The anchor
is dynamic via \`horizontalAnchor\` - the active menu option when horizontal navigation started from the menu, otherwise the
component's trigger/input (resolved through \`exitToTrigger\` when no anchor element exists).

* From the anchor, \`ArrowLeft\` enters \`leftNavigableOptions[0]\` and \`ArrowRight\` enters \`rightNavigableOptions[0]\`.
* Within a side, the arrow pointing away from the anchor moves outward and the opposite arrow moves inward; moving inward past
  index 0 returns focus to \`horizontalAnchor\`.
* At the far (outer) edge of a side the engine stops, unless \`wrapAroundHorizontal\` is enabled, in which case it wraps back to
  index 0 of that side.
* A horizontal key is only captured (preventing its default, e.g. caret movement) when there is somewhere to go and
  \`canEnterHorizontal(evt)\` allows it - input-based consumers override that hook to gate on caret position.

### navigableOptions registration contract

The engine never queries the DOM for options. Instead, a parent menu registers its option DOM nodes (via the base-class
component/element registration hooks) and exposes them through a reactive \`navigableOptions\` getter that the inheriting
component overrides. Movement, activation, and selection all operate on that array, so consumers stay in control of which
elements are navigable (e.g. filtering out hidden items).

### Configurable surface

Inheriting components tune behavior by overriding these members:

* \`navigableOptions\` (getter): the ordered array of focusable option elements. Defaults to an empty array.
* \`allowHorizontalNavigation\` (getter): when \`true\`, \`ArrowLeft\` / \`ArrowRight\` drive the horizontal navigation model described
  above. Defaults to \`false\`.
* \`leftNavigableOptions\` / \`rightNavigableOptions\` (getters): the ordered focusable controls on each side of the anchor (index 0
  nearest the anchor). Default to empty arrays.
* \`horizontalAnchor\` (getter): the element horizontal navigation returns to when moving inward past index 0. Defaults to the
  current vertical active option; falls back to \`exitToTrigger\` when none exists.
* \`wrapAround\` (getter/field): when \`true\`, moving past the last option wraps to the first and moving before the first wraps to
  the last. When \`false\`, the engine calls \`exitToTrigger\` at the boundaries so a trigger-based menu can return focus to its
  trigger. Defaults to \`false\`.
* \`wrapAroundHorizontal\` (getter): the horizontal-axis counterpart of \`wrapAround\`. Defaults to \`false\` (stop at the outer edge).
* \`menuCanOpen\` (getter): gates keyboard opening. Defaults to \`true\`.
* \`getActiveOption\`: returns the element a selection key should act on. The default returns the active horizontal option (if
  any) then the active vertical option; trigger-based menus override this to fall back to the trigger.
* \`hasCustomAction(key)\`: gates whether a captured key performs a custom action. Defaults to \`true\`.
* \`canEnterHorizontal(evt)\`: gates whether a horizontal key is captured (e.g. only when an input's caret is at the edge).
  Defaults to \`true\`.
* \`handleOpen\` / \`handleClose\`: open and close the menu. \`handleClose\` should clear \`active\` state from options.
* \`exitToTrigger\`: boundary behavior, a no-op by default.
* \`initialize\`: optional async hook run once during setup (before the \`keydown\` listener is attached) for selection/value setup.

### Adopting in a new component

1. Extend this class and attach metadata via \`static metadata = this.setMetadata(METADATA, import.meta.url)\`.
2. Register option DOM nodes on a child menu and expose them through a \`navigableOptions\` getter.
3. Set \`allowHorizontalNavigation\` and \`wrapAround\` to match the desired behavior, override \`handleOpen\` / \`handleClose\`
   (and \`exitToTrigger\` if the menu has a trigger), and expose \`leftNavigableOptions\` / \`rightNavigableOptions\` when the
   component has controls flanking the anchor.
4. Render the component's root element with \`{{this.setupComponent}}\` so \`baseElement\` is registered and the listener is wired up.
  `,
  extends: [pick(COMPONENT_STATE_ARGS, ['disabled'])],
  name: 'Keyboard Navigable',
});

/**
 * Abstract base that provides a configurable arrow-key navigation engine for menu-style SPDS components.
 *
 * @argument {boolean} disabled when true, all keyboard interaction is ignored
 *
 * @property {boolean} open whether the menu is currently open
 * @property {Element} activeOption the option element that currently has focus
 * @property {Element} activeHorizontalOption the left/right control that currently has focus, if any
 * @property {boolean} allowHorizontalNavigation enables ArrowLeft/ArrowRight handling (override)
 * @property {Array<Element>} leftNavigableOptions ordered controls to the left of the anchor (override)
 * @property {Array<Element>} rightNavigableOptions ordered controls to the right of the anchor (override)
 * @property {Element} horizontalAnchor element horizontal navigation returns to past index 0 (override)
 * @property {boolean} wrapAround wraps focus at list boundaries instead of exiting to a trigger (override)
 * @property {boolean} wrapAroundHorizontal wraps horizontal focus at the outer edge (override)
 * @property {Array<Element>} navigableOptions ordered list of focusable option elements (override)
 * @property {boolean} menuCanOpen gates whether keyboard input may open the menu (override)
 */
export default class SpdsCoreKeyboardNavigable extends Super {
  static metadata = this.setMetadata(METADATA, import.meta.url);

  @tracked open = false;
  @tracked activeOption;
  @tracked activeHorizontalOption;

  /**
   * ordered list of focusable option elements the engine navigates; override in inheriting components
   * @return {Array<Element>} the navigable option elements
   */
  get navigableOptions() {
    return [];
  }

  /**
   * whether ArrowLeft/ArrowRight should be routed to horizontal navigation; override to enable
   * @return {boolean}
   */
  get allowHorizontalNavigation() {
    return false;
  }

  /**
   * ordered controls to the left of the anchor (index 0 nearest the anchor); override to enable
   * @return {Array<Element>}
   */
  get leftNavigableOptions() {
    return [];
  }

  /**
   * ordered controls to the right of the anchor (index 0 nearest the anchor); override to enable
   * @return {Array<Element>}
   */
  get rightNavigableOptions() {
    return [];
  }

  /**
   * the element horizontal navigation returns to when moving inward past index 0; defaults to the
   * active vertical option (falls back to exitToTrigger when undefined); override to customize
   * @return {Element|undefined}
   */
  get horizontalAnchor() {
    return this.activeOption;
  }

  /**
   * whether focus wraps at list boundaries (true) or exits to a trigger (false); override to enable
   * @return {boolean}
   */
  get wrapAround() {
    return false;
  }

  /**
   * whether horizontal focus wraps at the outer edge of a side; override to enable
   * @return {boolean}
   */
  get wrapAroundHorizontal() {
    return false;
  }

  /**
   * gates whether keyboard input may open the menu; override to add conditions
   * @return {boolean}
   */
  get menuCanOpen() {
    return true;
  }

  willDestroy() {
    super.willDestroy(...arguments);

    this.baseElement?.removeEventListener('keydown', this.keyboardNav);
  }

  /**
   * runs the optional async initialize hook, then attaches the keydown listener once
   * invoked by the base-class setupComponent modifier
   */
  setup = async () => {
    await this.initialize?.();

    this.baseElement.addEventListener('keydown', this.keyboardNav);
  };

  /**
   * determines whether a key pressed while closed should open the menu
   * @param {string} key the pressed key
   * @return {boolean}
   */
  canTriggerKeyboardOpen(key) {
    return (isVerticalArrowKey(key) || isSelectionKey(key)) && (this.menuCanOpen ?? true);
  }

  /**
   * resolves the movement direction from the event (down/Tab = forward, up/Shift+Tab = backward)
   * @param {KeyboardEvent} evt
   * @return {number} 1 for forward, -1 for backward
   */
  determineDirection({ key, shiftKey }) {
    return isDownKey(key) && !(shiftKey && isShiftableKey(key)) ? 1 : -1;
  }

  /**
   * returns the element a selection key should act on; override to add fallbacks (e.g. trigger)
   * @return {Element|undefined}
   */
  getActiveOption() {
    return this.activeHorizontalOption ?? this.navigableOptions?.find(isActive);
  }

  /**
   * gates whether a captured key performs a custom action; override to filter keys
   * @return {boolean}
   */
  hasCustomAction() {
    return true;
  }

  /**
   * gates whether a horizontal key is captured (preventing its default); override for input
   * consumers to require, e.g., the caret to be at the field edge
   * @return {boolean}
   */
  canEnterHorizontal() {
    return true;
  }

  /**
   * opens the menu; override to run side effects (e.g. notifying consumers)
   */
  handleOpen = () => {
    this.open = true;
  };

  /**
   * closes the menu; override to clear active state from options and run side effects
   */
  handleClose = () => {
    this.open = false;
  };

  /**
   * top-level keydown handler: opens the menu when closed, otherwise parses the action
   */
  keyboardNav = evt => {
    if (this.args.disabled) return;

    if (!this.open) {
      this.openWithKeyboard(evt);
    } else {
      this.parseKeyAction(evt);
    }
  };

  /**
   * opens the menu in response to a triggering key and moves to an option once it has rendered
   * @param {KeyboardEvent} evt
   */
  async openWithKeyboard(evt) {
    if (!this.canTriggerKeyboardOpen(evt.key)) return;

    evt.preventDefault();
    this.handleOpen();

    if (!isSelectionKey(evt.key)) {
      // give the menu time to render before trying to move to an option
      await waitForLater(MENU_OPEN_DELAY);

      this.manageSelectedItem(evt);
    }
  }

  /**
   * routes a key pressed while the menu is open to selection, close, or movement
   * @param {KeyboardEvent} evt
   */
  parseKeyAction(evt) {
    let { key } = evt;

    if (!isCapturable(key)) return;
    if (isHorizontalArrowKey(key) && !this.canCaptureHorizontal(evt)) return;
    if (!this.hasCustomAction(key)) return;

    evt.preventDefault();

    if (isSelectionKey(key)) {
      this.selectActiveOption();
    } else if (isCloseKey(key)) {
      this.handleClose();
    } else {
      this.manageSelectedItem(evt);
    }
  }

  /**
   * activates the current selection; safe to call when nothing is active
   */
  selectActiveOption() {
    this.getActiveOption()?.click();
  }

  /**
   * routes movement keys to vertical or (when enabled) horizontal handling
   * @param {KeyboardEvent} evt
   */
  manageSelectedItem(evt) {
    if (isVerticalMovementKey(evt.key)) {
      this.handleVerticalPress(evt);
    } else if (this.allowHorizontalNavigation) {
      this.handleHorizontalPress(evt);
    }
  }

  /**
   * moves the active option up or down within navigableOptions; when a horizontal control is active,
   * a vertical key returns focus to the vertical active option instead of moving past it
   * @param {KeyboardEvent} evt
   */
  handleVerticalPress(evt) {
    let activeElement = this.getActiveOption();
    let direction = this.determineDirection(evt);
    let startingIndex = this.navigableOptions.findIndex(option => option === activeElement);

    if (indexInRange(this.navigableOptions.length, startingIndex)) {
      deactivate(this.navigableOptions[startingIndex]);
    }

    if (this.activeHorizontalOption) {
      this.deactivateHorizontalOption();
      this.updateVerticalSelection(
        this.navigableOptions.findIndex(option => option === this.activeOption)
      );
    } else {
      this.updateVerticalSelection(startingIndex + direction);
    }
  }

  /**
   * whether a horizontal key should be captured: horizontal navigation is enabled, the consumer
   * allows entering (e.g. caret at the edge), and there is somewhere to move
   * @param {KeyboardEvent} evt
   * @return {boolean}
   */
  canCaptureHorizontal(evt) {
    return (
      this.allowHorizontalNavigation &&
      this.canEnterHorizontal(evt) &&
      this.hasHorizontalDestination(evt)
    );
  }

  /**
   * whether there is a horizontal control to move to for the pressed key
   * @param {KeyboardEvent} evt
   * @return {boolean}
   */
  hasHorizontalDestination({ key }) {
    if (this.activeHorizontalOption) return true;

    let side = isLeftKey(key) ? this.leftNavigableOptions : this.rightNavigableOptions;

    return side.length > 0;
  }

  /**
   * traverses the left/right control lists: enters a side from the anchor, moves outward/inward
   * within it, returns to the anchor past index 0, and wraps or stops at the outer edge
   * @param {KeyboardEvent} evt
   */
  handleHorizontalPress({ key }) {
    let current = this.activeHorizontalOption;

    if (!current) {
      let side = isLeftKey(key) ? this.leftNavigableOptions : this.rightNavigableOptions;

      if (side.length) this.focusHorizontalOption(side[0]);

      return;
    }

    let onRight = this.rightNavigableOptions.includes(current);
    let side = onRight ? this.rightNavigableOptions : this.leftNavigableOptions;
    let movingOutward = onRight ? isRightKey(key) : isLeftKey(key);
    let nextIndex = side.indexOf(current) + (movingOutward ? 1 : -1);

    if (indexInRange(side.length, nextIndex)) {
      this.focusHorizontalOption(side[nextIndex]);
    } else if (nextIndex < 0) {
      this.returnToHorizontalAnchor();
    } else if (this.wrapAroundHorizontal) {
      this.focusHorizontalOption(side[0]);
    }
  }

  /**
   * focuses a horizontal control, clearing the previously active horizontal and vertical options
   * @param {Element} element
   */
  focusHorizontalOption(element) {
    deactivate(this.activeHorizontalOption);
    deactivate(this.activeOption);
    this.activeHorizontalOption = element;
    updateElementFocus(element);
  }

  /**
   * returns focus to the horizontal anchor (active option or, failing that, the trigger)
   */
  returnToHorizontalAnchor() {
    this.deactivateHorizontalOption();

    if (this.horizontalAnchor) {
      updateElementFocus(this.horizontalAnchor);
    } else {
      this.exitToTrigger();
    }
  }

  /**
   * clears the active horizontal control and its focus state
   */
  deactivateHorizontalOption() {
    deactivate(this.activeHorizontalOption);
    this.activeHorizontalOption = undefined;
  }

  /**
   * focuses the option at index, wrapping or exiting to the trigger at the boundaries
   * @param {number} index the target index (may be out of range)
   */
  updateVerticalSelection(index) {
    let length = this.navigableOptions.length;
    let lastIndex = length - 1;

    if (indexInRange(length, index)) {
      this.updateFocus(index);
      return;
    }

    if (this.wrapAround) {
      this.updateFocus(index < 0 ? lastIndex : 0);
    } else if (index < -1) {
      // moving up from the trigger lands on the last option
      this.updateFocus(lastIndex);
    } else {
      this.exitToTrigger();
    }
  }

  /**
   * boundary behavior when not wrapping; override to return focus to a trigger
   */
  exitToTrigger() {}

  /**
   * focuses the option at the given index and records it as the active option
   * @param {number} index
   */
  updateFocus(index) {
    this.activeOption = this.navigableOptions[index];
    updateElementFocus(this.activeOption);
  }
}

export const CORE_KEYBOARD_NAVIGABLE_METADATA = SpdsCoreKeyboardNavigable.metadata;
